'use client'

import { IMediaItem, IMediaMetadata } from '@/lib/types/media/core'
import { showToast } from '@/lib/utils/toast'
import { useCallback, useState } from 'react'
import { MediaPreview } from '../core/MediaPreview'
import { MediaMetadataForm } from './MediaMetadataForm'

export interface MediaEditorProps {
  media: IMediaItem
  onSave?: (media: IMediaItem | null) => void
  onCancel?: () => void
  aspectRatio?: 'square' | 'video' | 'auto'
}

export function MediaEditor({ media: initialMedia, onSave, onCancel, aspectRatio }: MediaEditorProps) {
  const [media, setMedia] = useState(initialMedia)
  const [isEditing, setIsEditing] = useState(false)

  const handleReplace = useCallback(
    async (file: File) => {
      try {
        // Upload using fetch to /api/upload
        const formData = new FormData()
        formData.append('file', file)
        formData.append('placement', media.placement)
        formData.append('userId', media.metadata?.ownerId || '')
        formData.append('uploadId', `upload-${Date.now()}`)

        const response = await fetch('/api/upload', {
          method: 'POST',
          body: formData
        })

        if (!response.ok) {
          throw new Error('Upload failed')
        }

        const result = await response.json()
        if (!result.success || !result.media) {
          throw new Error(result.error || 'Upload failed')
        }

        setMedia(result.media)
        onSave?.(result.media)
        showToast({ title: 'Media replaced successfully', color: 'success', timeout: 3000 })
      } catch (error) {
        showToast({ title: 'Failed to replace media', color: 'danger', timeout: 5000 })
        console.error('Replace error:', error)
      }
    },
    [media, onSave]
  )

  const handleMetadataUpdate = useCallback(
    async (metadata: Partial<IMediaMetadata>) => {
      try {
        // Update media metadata via API
        const response = await fetch(`/api/media/${media.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ metadata })
        })

        if (!response.ok) {
          throw new Error('Update failed')
        }

        const result = await response.json()
        if (!result.success || !result.media) {
          throw new Error(result.error || 'Update failed')
        }
        const updated = result.media

        setMedia(updated)
        setIsEditing(false)
        onSave?.(updated)
        showToast({ title: 'Metadata updated successfully', color: 'success', timeout: 3000 })
      } catch (error) {
        showToast({ title: 'Failed to update metadata', color: 'danger', timeout: 5000 })
        console.error('Update error:', error)
      }
    },
    [media.id, onSave]
  )

  const handleRemove = useCallback(async () => {
    if (!media.metadata?.ownerId) {
      showToast({ title: 'Cannot delete media: missing owner ID', color: 'danger', timeout: 5000 })
      return
    }
    try {
      const response = await fetch(`/api/media/${media.id}?userId=${media.metadata.ownerId}`, {
        method: 'DELETE'
      })
      if (!response.ok) {
        throw new Error('Delete failed')
      }
      showToast({ title: 'Media deleted successfully', color: 'success', timeout: 3000 })
      onSave?.(null)
    } catch (error) {
      showToast({ title: 'Failed to delete media', color: 'danger', timeout: 5000 })
      console.error('Delete error:', error)
    }
  }, [media.id, media.metadata?.ownerId, onSave])

  return (
    <div className='space-y-4'>
      <MediaPreview
        media={media}
        onReplace={handleReplace}
        onRemove={handleRemove}
        onEdit={() => setIsEditing(true)}
        aspectRatio={aspectRatio}
      />

      {isEditing && (
        <MediaMetadataForm media={media} onSave={handleMetadataUpdate} onCancel={() => setIsEditing(false)} />
      )}
    </div>
  )
}
